import { useState } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Send, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const CommentSection = ({ targetType, targetId, comments, onCommentAdded, onCommentDeleted }) => {
  const { user } = useAuth();
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/comments`, {
        content: content.trim(),
        target_type: targetType,
        target_id: targetId,
      });
      onCommentAdded(response.data);
      setContent("");
      toast.success("Comment added!");
    } catch (error) {
      toast.error("Failed to add comment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId) => {
    try {
      await axios.delete(`${API}/comments/${commentId}`);
      onCommentDeleted(commentId);
      toast.success("Comment deleted");
    } catch (error) {
      toast.error("Failed to delete comment");
    }
  };

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-white font-['Outfit']">Comments</h3>
      
      {/* Comment Form */}
      {user ? (
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="comment-form">
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Share your thoughts..."
            className="input-dark min-h-[100px] resize-none"
            data-testid="comment-input"
          />
          <Button 
            type="submit" 
            disabled={submitting || !content.trim()}
            className="btn-primary flex items-center gap-2"
            data-testid="comment-submit"
          >
            <Send className="w-4 h-4" />
            {submitting ? "Posting..." : "Post Comment"}
          </Button>
        </form>
      ) : (
        <p className="text-slate-400 text-sm">
          <a href="/login" className="text-yellow-500 hover:underline">Sign in</a> to leave a comment.
        </p>
      )}

      {/* Comments List */}
      <div className="space-y-4">
        {comments.length === 0 ? (
          <p className="text-slate-500 text-sm">No comments yet. Be the first!</p>
        ) : (
          comments.map((comment) => (
            <div 
              key={comment.id} 
              className="card-surface p-4"
              data-testid={`comment-${comment.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-medium text-white">{comment.user_name}</span>
                    <span className="text-xs text-slate-500 font-mono">
                      {new Date(comment.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-slate-300 text-sm">{comment.content}</p>
                </div>
                {user && (user.id === comment.user_id || user.role === "admin") && (
                  <button
                    onClick={() => handleDelete(comment.id)}
                    className="text-slate-500 hover:text-red-500 transition-colors"
                    data-testid={`delete-comment-${comment.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CommentSection;
